import React, { useCallback, useState } from "react";

import { formatPrice } from "@app/scripts/utils";
import { Button } from "@app/scripts/react-general-components/Button";

import type { Cart } from "@app/scripts/types";
import { useShopifyCart } from "@app/scripts/react-hooks/shopify";
import { useLocalisation } from "@app/scripts/react-hooks/localisation";

type Props = {
  cart: Cart | null;
  totalDiscount: string | null;
  goToCheckout: (e: any, toggleOpen?: () => void) => Promise<void>;
  additionalCheckout?: string | null;
  cartTotal: number;
  toggleOpen?: (e?: React.MouseEvent<HTMLElement, MouseEvent>, _open?: boolean) => void;
  view?: "cart_page";
  cartBeforeDiscountPrice: string;
  error: ReturnType<typeof useShopifyCart>["error"];
};

export const CartOrderSummary: React.FC<Props> = ({
  totalDiscount,
  cart,
  goToCheckout,
  additionalCheckout,
  cartTotal,
  toggleOpen,
  view,
  cartBeforeDiscountPrice,
  error,
}) => {
  const { localisation } = useLocalisation();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const handleContinueShopClick = useCallback(
    () => (view === "cart_page" ? (window.location.href = "/") : toggleOpen?.()),
    [toggleOpen, view]
  );

  const handleSummaryClick = () => {
    setSummaryOpen(!summaryOpen);
  };

  return (
    <div className="cart__order-summary text-brand-dark-grey">
      <h2 onClick={handleSummaryClick} className="cart__order-summary__title h4 flex items-center justify-between cursor-pointer">
        {summaryOpen ? "Close" : "Open"} Order Summary
        <span className={summaryOpen ? "hidden" : "block"}>
          <ui-icon width="16" height="16" name="icon-plus"></ui-icon>
        </span>
        <span className={summaryOpen ? "block" : "hidden"}>
          <ui-icon width="16" height="16" name="icon-minus"></ui-icon>
        </span>
      </h2>

      <div className="cart__order-summary__details">
        {summaryOpen ? (
          <>
            <div className={`${additionalCheckout ? "mb-4" : "mb-2"} cart__discount`}>
              <span className="text-md">Subtotal</span>
              <span className="text-md">{cartBeforeDiscountPrice}</span>
            </div>
            {totalDiscount && (
              <div className={`${additionalCheckout ? "mb-4" : "mb-2"} cart__discount`}>
                <span className="text-md">Discounts</span>
                <span className="text-md">-{totalDiscount}</span>
              </div>
            )}
            <p className="text-sm text-secondary-grey cart__shipping">
              Shipping, Gift Cards & Promo Codes calculated at checkout.
            </p>
            {error && (
              <small className="cart__error product-card__add-to-cart__error bg-brand-error text-brand-white">
                {error}
              </small>
            )}
          </>
        ) : null}

        <div className={`${additionalCheckout ? "mb-4" : "mb-2"} cart__total`}>
          <span className="h3 bold">Total</span>
          <span className="h3 bold">{cart && formatPrice(cartTotal, localisation?.currency_code)}</span>
        </div>

        <div className="cart__checkout">
          <Button
            onClick={(e) => goToCheckout(e, toggleOpen)}
            disabled={!!error}
            label="Checkout"
            variant={additionalCheckout ? "primary" : "secondary"}
            className="fullwidth"
          />

          {additionalCheckout ? (
            <div className="cart__express-checkout">
              <h3 className="cart__express-checkout-title">Express Checkout</h3>
              <p className="text-sm text-secondary-grey cart__express-checkout__shipping">
                Gift Cards and promo codes cannot be applied when using express checkout
              </p>
              <div dangerouslySetInnerHTML={{ __html: additionalCheckout }} />
            </div>
          ) : null}

          <Button
            onClick={handleContinueShopClick}
            ariaLabel="Continue Shopping"
            label="Continue Shopping"
            variant="text"
          />
        </div>
      </div>
    </div>
  );
};
